import { FC } from "react";
import InfoIndicator from "@app/shared/InfoIndicator";
import { QESButton } from "@app/shared/qes-standards/Button";

type ClientDetailRow = {
    avgEndToEnd:                        number | string | null;
    avgPubQueueWait:                    number | string | null;
    avgPubRunTime:                      number | string | null;
    avgQueueWait:                       number | string | null;
    avgRunTime:                         number | string | null;
    avgTimeToPublish:                   number | string | null;
    hasZipSummaries:                    boolean | null;
    isDualMembership:                   boolean | null;
    outliers:                           number | null;
    project:                            string | null;
    projectId:                          number | null;
    quarantined:                        number | null;
    showAffiliationsInsights:           boolean | null;
    showNetworkIntegrityDashboard:      boolean | null;
    showNextClosestNetworkProviders:    boolean | null;
    showPciData:                        boolean | null;
    totalRuns:                          number | null;
    useSnowflakeProviderSetsForAnalysis: boolean | null;
};

type Props = {
    clientName:  string | null;
    hasMore:     boolean;
    loadingMore: boolean;
    onLoadMore:  () => void;
    rows:        ClientDetailRow[];
};

const fmt = (v: number | string | null | undefined): string =>
    v === null || v === undefined ? '—' : typeof v === 'string' ? v : v.toFixed(1);

const PROJECT_OPTION_LABELS: Array<{ flag: keyof ClientDetailRow; label: string }> = [
    { flag: 'hasZipSummaries', label: 'Zip Summaries' },
    { flag: 'showNextClosestNetworkProviders', label: 'NCP' },
    { flag: 'showPciData', label: 'PCI' },
    { flag: 'showNetworkIntegrityDashboard', label: 'NI' },
    { flag: 'useSnowflakeProviderSetsForAnalysis', label: 'Snowflake Provider Sets' },
    { flag: 'showAffiliationsInsights', label: 'PA' },
    { flag: 'isDualMembership', label: 'Dual Membership' },
];

const formatProjectOptions = (r: ClientDetailRow): string =>
    PROJECT_OPTION_LABELS.filter(o => r[o.flag]).map(o => o.label).join(', ') || '—';

const PROJECT_OPTIONS_TOOLTIP = (
    <>
        NCP — Next Closest Network Providers<br />
        PCI — Provider Claims Insights<br />
        NI — Network Integrity<br />
        PA — Provider Affiliations
    </>
);

export const JobPerformanceClientDetailPanel: FC<Props> = ({ clientName, hasMore, loadingMore, onLoadMore, rows }) => (
    <div className="jp-client-panel">
        <div className="jp-client-panel__title">
            Project Run Detail — {clientName ?? ''}
        </div>
        <table className="jp-client-panel__table">
            <thead>
                <tr>
                    <th>Project ID</th>
                    <th>Project Name</th>
                    <th>Project Options <InfoIndicator className="jp-section-header__icon" tooltipTitle={PROJECT_OPTIONS_TOOLTIP} tooltipWidth="280px" /></th>
                    <th>Total Runs</th>
                    <th>Avg Queue Wait</th>
                    <th>Avg Run Time</th>
                    <th>Avg Time to Publish</th>
                    <th>Avg Pub Queue Wait</th>
                    <th>Avg Pub Run Time</th>
                    <th>Avg Total E2E</th>
                    <th>Outliers</th>
                    <th>Quarantined</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.projectId ?? i}>
                        <td className="col-project-id">{r.projectId}</td>
                        <td>{r.project}</td>
                        <td>{formatProjectOptions(r)}</td>
                        <td className="col-timing">{r.totalRuns?.toLocaleString() ?? '—'}</td>
                        <td className="col-timing">{fmt(r.avgQueueWait)}</td>
                        <td className="col-timing">{fmt(r.avgRunTime)}</td>
                        <td className="col-timing">{fmt(r.avgTimeToPublish)}</td>
                        <td className="col-timing">{fmt(r.avgPubQueueWait)}</td>
                        <td className="col-timing">{fmt(r.avgPubRunTime)}</td>
                        <td className="col-e2e">{fmt(r.avgEndToEnd)}</td>
                        <td>
                            <span className={r.outliers > 0 ? 'jp-chip jp-chip--active' : 'jp-chip jp-chip--muted'}>
                                {r.outliers ?? 0}
                            </span>
                        </td>
                        <td>
                            <span className={r.quarantined > 0 ? 'jp-chip jp-chip--active' : 'jp-chip jp-chip--muted'}>
                                {r.quarantined ?? 0}
                            </span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        {hasMore && (
            <QESButton
                className="jp-client-panel__load-more"
                disabled={loadingMore}
                onClick={onLoadMore}
                qesTestId="job-performance__client-panel-load-more"
                qesType="outline"
            >
                {loadingMore ? 'Loading…' : 'See More'}
            </QESButton>
        )}
    </div>
);
